import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Warm the hover pairing as the card approaches the viewport instead of preloading
    // every card at connect — see #observeHoverPreload.
    this.#observeHoverPreload();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#hoverPreloadObserver?.disconnect();
    this.#hoverPreloadObserver = null;
  }

  /** @type {IntersectionObserver | null} */
  #hoverPreloadObserver = null;

  /** @type {boolean} Whether this card has been close enough to warm its hover image. */
  #hoverPreloadArmed = false;

  /** @type {(() => void) | null} Cancels a hover swap that is waiting on an image. */
  #cancelPendingHoverSwap = null;

  /**
   * Fetches the hover image before the pointer arrives.
   *
   * The paired slide's `<img>` ships `loading="lazy"`, and a lazy image that sits in an
   * off-screen slide is not fetched until it is revealed — so the first hover could swap
   * to an empty slide (measured: blank for up to ~1.3s on a slow profile, invisible on a
   * fast line, which is why it only shows on some machines). Warming is deliberately
   * timid: only where the pointer can hover, only within 200px of the viewport, and only
   * once the image already on screen has loaded — so it never competes with the visible
   * content for bandwidth. `#revealHoverSlide` is what guarantees no blank frame.
   */
  #observeHoverPreload() {
    if (!this.#pointerCanHover()) return;

    if (typeof IntersectionObserver === 'undefined') {
      this.#hoverPreloadArmed = true;
      return;
    }

    this.#hoverPreloadObserver?.disconnect();
    this.#hoverPreloadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#hoverPreloadObserver?.disconnect();
        this.#hoverPreloadObserver = null;
        this.#hoverPreloadArmed = true;
        this.#preloadHoverImageWhenSettled();
      },
      { rootMargin: '200px 0px' }
    );

    this.#hoverPreloadObserver.observe(this.refs.cardGallery ?? this);
  }

  /**
   * @returns {boolean} Whether the primary pointer can hover (i.e. a mouse/trackpad).
   */
  #pointerCanHover() {
    return typeof window.matchMedia === 'function'
      ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
      : true;
  }

  /**
   * Warms the pairing after the image on screen has loaded, at the next idle moment.
   */
  #preloadHoverImageWhenSettled() {
    const lead = this.#visibleImage();

    if (lead && !(lead.complete && lead.naturalWidth > 0)) {
      lead.addEventListener('load', () => this.#scheduleHoverPreload(), { once: true });
      return;
    }

    this.#scheduleHoverPreload();
  }

  #scheduleHoverPreload() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => this.#preloadHoverImage(), { timeout: 2000 });
    } else {
      setTimeout(() => this.#preloadHoverImage(), 200);
    }
  }

  /**
   * @returns {HTMLImageElement | undefined} The image currently on screen for this card.
   */
  #visibleImage() {
    const slides = [...(this.refs.slideshow?.refs.slides ?? [])];
    const currentSlide = slides.find((slide) => slide.getAttribute('aria-hidden') === 'false');

    return (
      currentSlide?.querySelector('img') ??
      slides.find((slide) => slide.hasAttribute('variant-image'))?.querySelector('img')
    );
  }

  /**
   * The slide id paired with the image on screen. `snippets/card-gallery.liquid` emits it
   * on each variant image slide. Falls back to the first variant image because the
   * slideshow only marks the visible slide once it has initialised.
   * @param {HTMLElement[]} slides
   * @returns {string | null}
   */
  #hoverPairId(slides) {
    const currentSlide = slides.find((slide) => slide.getAttribute('aria-hidden') === 'false');

    const pairId =
      currentSlide?.getAttribute('data-hover-slide-id') ??
      slides.find((slide) => slide.hasAttribute('variant-image'))?.getAttribute('data-hover-slide-id');

    return pairId ?? null;
  }

  /**
   * Drops `loading="lazy"` from the paired slide so the browser fetches it before the
   * pointer arrives.
   */
  #preloadHoverImage() {
    const slideshow = this.refs.slideshow;

    if (!slideshow) return;

    const slides = [...(slideshow.refs.slides ?? [])];
    const pairId = this.#hoverPairId(slides);

    if (!pairId) return;

    slides
      .find((slide) => slide.getAttribute('slide-id') === pairId)
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute('loading');
  }

  /**
   * Reveals the paired slide, but only once its image can paint. A hover on a connection
   * that has not fetched the shot yet keeps the lead image up for a moment instead of
   * showing an empty card, and a pointer that leaves before the fetch finishes cancels it.
   * @param {import('slideshow').Slideshow} slideshow
   * @param {string} slideId
   */
  #revealHoverSlide(slideshow, slideId) {
    this.#cancelHoverSwap();

    const target = [...(slideshow.refs.slides ?? [])].find(
      (slide) => slide.getAttribute('slide-id') === slideId
    );
    const image = target?.querySelector('img');

    if (!image || (image.complete && image.naturalWidth > 0)) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    const swap = () => {
      this.#cancelHoverSwap();
      slideshow.select({ id: slideId }, undefined, { animate: false });
    };

    // Reveal a partially-decoded image (the browser paints what it has), but never an empty
    // slot: if nothing has arrived after 1.2s the lead image stays up. The load listener
    // below survives the timer, so the card still swaps the moment the image lands — as
    // long as the pointer is still on the card.
    const timer = setTimeout(() => {
      if (image.naturalWidth > 0) swap();
    }, 1200);

    const onLoad = () => swap();

    image.addEventListener('load', onLoad, { once: true });

    this.#cancelPendingHoverSwap = () => {
      clearTimeout(timer);
      image.removeEventListener('load', onLoad);
      this.#cancelPendingHoverSwap = null;
    };
  }

  #cancelHoverSwap() {
    this.#cancelPendingHoverSwap?.();
  }

  #preloadNextPreviewImage() {
    const slideshow = this.refs.slideshow;

    if (!slideshow) return;

    // Warm the hover pairing first — it is the image a pointer-enter reveals.
    this.#preloadHoverImage();

    const slides = [...(slideshow.refs.slides ?? [])];

    const currentSlide = slides.find(
      (slide) => slide.getAttribute('aria-hidden') === 'false'
    );

    const pairedId = this.#hoverPairId(slides);

    if (!currentSlide) return;

    const currentIndex = slides.indexOf(currentSlide);

    const nextSlide = slides
      .slice(currentIndex + 1)
      .find(
        (slide) =>
          !slide.hasAttribute('variant-image') &&
          slide.getAttribute('slide-id') !== pairedId
      );

    nextSlide
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute('loading');
  }
  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // Reset manually selected slide state.
    this.#previousSlideIndex = null;

    // A colour has just been applied, so a hover preview must not put the old image back
    // when the pointer leaves the card — the chosen variant's image has to stay.
    this.#slideBeforeHover = null;

    // Same reason: a hover swap still waiting on an image must not land on top of the
    // image the customer just chose.
    this.#cancelHoverSwap();

    // Select the newly selected variant image.
    this.#updateVariantImages();

    this.removeAttribute('data-no-swatch-selected');

    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;

    if (!slideshow || !this.variantPicker?.selectedOption) return;

    const selectedImageId = this.variantPicker.selectedOption.dataset.optionMediaId;

    if (!selectedImageId) return;

    const slides = [...(slideshow.refs.slides ?? [])];

    // Prefer the variant-marked slide, then fall back to any slide showing that media: a
    // swatch's image is not always attached to the variant in the card markup.
    const selectedVariantSlide =
      slides.find(
        (slide) =>
          slide.hasAttribute('variant-image') &&
          slide.getAttribute('slide-id') === selectedImageId
      ) ?? slides.find((slide) => slide.getAttribute('slide-id') === selectedImageId);

    if (!selectedVariantSlide) return;

    slideshow.select(
      { id: selectedImageId },
      undefined,
      { animate: false }
    );

    this.#preloadNextPreviewImage();
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  #variantImageSelected = false;

  /**
   * The slide the gallery was showing before a hover preview started, so leaving the card
   * restores exactly that image. The theme's own reset only steps back one slide, which was
   * correct while hover advanced one slide — a pairing can sit several slides away.
   * @type {string | null}
   */
  #slideBeforeHover = null;

  /**
   * Records the slide currently on screen, once per hover sequence.
   * @param {Slideshow} slideshow
   */
  #rememberSlideBeforeHover(slideshow) {
    if (this.#slideBeforeHover !== null) return;

    const current = [...(slideshow.refs.slides ?? [])].find(
      (slide) => slide.getAttribute('aria-hidden') === 'false'
    );

    this.#slideBeforeHover = current?.getAttribute('slide-id') ?? null;
  }

  /**
   * Restores the slide recorded before the hover preview.
   * @returns {boolean} Whether a slide was restored.
   */
  #restoreSlideBeforeHover() {
    const { slideshow } = this.refs;
    const id = this.#slideBeforeHover;

    this.#slideBeforeHover = null;

    if (!slideshow || !id) return false;

    slideshow.select({ id }, undefined, { animate: false });

    return true;
  }

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }

    // The slideshow marks the visible slide only when it initialises, which can land after
    // the observer fired — and a swatch change swaps the image — so re-warm the pairing
    // for the slide now on screen. Only for cards already close to the viewport.
    if (this.#hoverPreloadArmed) {
      this.#preloadHoverImage();
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    this.#rememberSlideBeforeHover(slideshow);
    slideshow.select({ id }, undefined, { animate: false });
    this.#preloadNextPreviewImage();
  }

  /**
   * Previews the colour's angled/lifestyle image.
   *
   * The pairing is resolved server-side (snippets/card-gallery.liquid) and emitted as
   * data-hover-slide-id on each variant image slide, because image alt text is empty
   * across the catalogue — the previous alt-text lookup never matched anything.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const slides = [...(slideshow.refs.slides ?? [])];

    const visibleSlide = slides.find((slide) => slide.getAttribute('aria-hidden') === 'false');

    // Whichever slide the customer sees may itself carry the pairing (products with no colour
    // swatches). Otherwise use the variant image on screen — falling back to the swatch
    // picker's selection and then to any variant image, so a repeat hover still works.
    const sourceSlide =
      (visibleSlide?.getAttribute('data-hover-slide-id') ? visibleSlide : null) ??
      slides.find(
        (slide) =>
          slide.hasAttribute('variant-image') &&
          slide.getAttribute('aria-hidden') === 'false' &&
          slide.getAttribute('data-hover-slide-id')
      ) ??
      slides.find(
        (slide) =>
          slide.hasAttribute('variant-image') &&
          slide.getAttribute('slide-id') ===
            this.variantPicker?.selectedOption?.dataset.optionMediaId &&
          slide.getAttribute('data-hover-slide-id')
      ) ??
      slides.find(
        (slide) =>
          slide.hasAttribute('variant-image') && slide.getAttribute('data-hover-slide-id')
      );

    if (!sourceSlide) return;

    const hoverSlideId = sourceSlide.getAttribute('data-hover-slide-id');

    // No angled/lifestyle shot for this colour — leave the image alone rather than
    // showing a different colour's photo.
    if (!hoverSlideId) return;

    this.#rememberSlideBeforeHover(slideshow);

    // Instant rather than animated: a pairing can sit several slides away, and the animated
    // far-jump reorders the DOM behind a `#disabled` flag — which looks like a jolt and can
    // swallow the reset when the pointer leaves before the dance finishes.
    this.#revealHoverSlide(slideshow, hoverSlideId);
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    // A hover shot that is still being fetched must not flip the card after the pointer left.
    this.#cancelHoverSwap();

    const { slideshow } = this.refs;

    // Put back exactly the slide the customer was looking at before the hover.
    if (this.#restoreSlideBeforeHover()) return;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.#restoreSlideBeforeHover()) return;

    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;

      if (id) {
        slideshow.select(
          { id },
          undefined,
          { animate: false }
        );

        return;
      }
    }

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');

    if (
      initialSlide &&
      slideshow.slides?.includes(initialSlide) &&
      slideId
    ) {
      slideshow.select(
        { id: slideId },
        undefined,
        { animate: false }
      );

      return;
    }

    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
